// @flow

// Hocs
import ForgotPasswordContainer from '../containers/ForgotPassword'

// Components
import { Errors } from 'hfty-components-errors'
import Textfield from '@react-mdc/textfield'
import FormField from '@react-mdc/form-field'
import Button from '@react-mdc/button'
import { Link } from 'react-router-dom'

// Libs
import React, { Component } from 'react'

// Constants
import { IDENTIFIER_PROP } from 'hfty-auth'
import { SUPPORT_PHONE_NUMBER } from 'hfty'

// Utils
import traverse from 'fc-utils/traverse'

// Images
import logoImg from 'hfty-images/src/FE_Soils_Logo_WhiteBg.png'

// Translations
import messages from '../messages'
import { FormattedMessage } from 'react-intl'

// Styles
import { white, primary, grey, error } from 'hfty-styles/src/colors'

// Local Types
type Props = {
  operation: ?Object,
  actions: {
    onSubmit: Function,
    onChange: Function,
  },
}

// Styles
const styles = {
  container: {
    flex: '1 1 auto',
    display: 'flex',
    backgroundColor: '#fafafa',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#333',
    flexDirection: 'column',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 5,
    padding: '0 10px',
    flex: '1 1 auto',
    minHeight: '6vh',
    minWidth: '23vh',
    color: grey(),
  },
  textField: {
    marginBottom: '20px',
    width: '80%',
    textAlign: 'center',
  },
  textFieldError: {
    color: error(),
  },
  component: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    width: '80%',
    maxWidth: 350,
    margin: 5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: white(),
    padding: 20,
    boxShadow: '0 0 3px rgba(0,0,0,0.3)',
    flex: '1 1 auto',
    justifyContent: 'space-around',
  },
  successMessage: {
    color: primary(),
  },
  errorMessage: {
    textAlign: 'justify',
  },
  callUsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5em',
    marginTop: 10,
    fontWeight: 500,
    textAlign: 'center',
  },
  link: {
    color: primary(),
  },
  divSpace: {
    marginLeft: 5,
    cursor: 'pointer',
  },
  formField: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  banner: {
    display: 'block',
    background: primary(),
    position: 'absolute',
    top: 0,
    left: 0,
    height: '4.4rem',
    width: '100%',
  },
  logo: {
    display: 'block',
    margin: 'auto',
    height: '3rem',
    position: 'relative',
    top: '18%',
  },
}

// Local constants
const PREFIX = 'ForgotPassword'

// Getters
const getIdentifierLabel = traverse(['operation', 'model', IDENTIFIER_PROP], { default: '' })

// Exports
export class ForgotPasswordForm extends Component {
  props: Props
  onChange: Function

  constructor(props: Props) {
    super(props)

    this.state = {
      fieldError: null,
    }

    this.onChange = this.onChange.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
  }

  onChange(e: Event & { currentTarget: HTMLInputElement }) {
    this.props.actions.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [IDENTIFIER_PROP]: e.currentTarget.value,
      },
    })
  }

  onSubmit(e: Event & { currentTarget: HTMLInputElement }) {
    e.preventDefault()
    let { model } = this.props.operation

    if (!model || !model.identifier) {
      this.setState({
        fieldError: `${PREFIX}.enterUsernameOrEmailError`,
      })
    } else if (model.identifier) {
      this.setState({
        fieldError: null,
      })
      this.props.actions.onSubmit()
    }
  }

  renderForm(): ?React.Element<any> {
    const { errors = [] } = this.props.operation
    const prefixedErrors = errors.map((errorObj: Object): Object => ({ error: `${PREFIX}.${errorObj.error}` }))

    return (
      <div style={styles.component}>
        <div style={styles.banner}>
          <img src={logoImg} style={styles.logo} />
        </div>

        <form style={styles.form} onSubmit={this.onSubmit}>
          <div style={styles.title}>
            <p>
              <FormattedMessage {...messages[`${PREFIX}.forgotPasswordPrompt`]} />
            </p>
          </div>
          <Errors errors={prefixedErrors} messages={messages} style={styles.errorMessage} />
          <div style={styles.textFieldError}>
            {this.state.fieldError ? <FormattedMessage {...messages[this.state.fieldError]} /> : null}
          </div>
          <div style={styles.textField}>
            <FormField style={styles.formField}>
              <Textfield style={{ width: '100%' }}>
                <Textfield.Label htmlFor="identifier">
                  <FormattedMessage {...messages[`${PREFIX}.usernameOrEmail`]} />
                </Textfield.Label>
                <Textfield.Input id={'identifier'} value={getIdentifierLabel(this.props)} onChange={this.onChange} />
              </Textfield>
            </FormField>
          </div>
          <FormField style={styles.formField}>
            <Button raised primary onClick={this.onSubmit}>
              <FormattedMessage {...messages[`${PREFIX}.continue`]} />
            </Button>
          </FormField>
        </form>
      </div>
    )
  }

  renderSuccessMessage(): ?React.Element<any> {
    return (
      <div style={styles.form}>
        <p style={styles.successMessage}>
          <FormattedMessage {...messages[`${PREFIX}.forgotPasswordInstructions`]} />
        </p>
        <Link style={styles.divSpace} to={'/'}>
          <FormattedMessage {...messages[`${PREFIX}.goToLoginPage`]} />
        </Link>
      </div>
    )
  }

  render(): ?React.Element<any> {
    return (
      <div style={styles.container}>
        <div style={styles.component}>
          {!this.props.operation.success ? this.renderForm() : this.renderSuccessMessage()}
        </div>
        {this.props.operation.success ? null : (
          <div style={styles.callUsWrapper}>
            <div>
              <FormattedMessage {...messages[`${PREFIX}.forgotUserNameAndEmail`]} />
            </div>
            <div>
              <FormattedMessage {...messages[`${PREFIX}.callUs`]} />:
              {' '}
              <a href={`tel:${SUPPORT_PHONE_NUMBER}`} style={styles.link}>
                {SUPPORT_PHONE_NUMBER}
              </a>
            </div>
          </div>
        )}
      </div>
    )
  }
}

export default ForgotPasswordContainer(ForgotPasswordForm)
